import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import axios from 'axios';

const AboutUs = () => {
  const navigate = useNavigate();
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/page/about-us`);
      setPageContent(response.data);
    } catch (error) {
      console.error('Error fetching page content:', error);
      // Set default content if API fails
      setPageContent(getDefaultContent());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultContent = () => ({
    sections: {
      hero: {
        title: 'About College Kampus',
        subtitle: 'Your trusted partner in college admission predictions',
        description: 'College Kampus is India\'s leading JEE college predictor platform. We help thousands of students make informed decisions about their college admissions with AI-powered predictions, comprehensive data analysis, and expert guidance. Our mission is to simplify the college selection process and ensure every student finds their perfect academic match.'
      },
      mission: {
        title: 'Our Mission',
        content: 'To empower every aspiring engineer with accurate, data-driven college admission predictions and help them achieve their educational dreams. We believe that every student deserves access to reliable information and tools that can guide them toward the right college and career path.'
      },
      vision: {
        title: 'Our Vision',
        content: 'To become India\'s most trusted and comprehensive college prediction platform, helping millions of students nationwide find their perfect college match. We envision a future where college admissions are transparent, accessible, and stress-free for all students and their families.'
      },
      services: {
        title: 'Our Services',
        items: [
          {
            title: 'JEE Rank Predictions',
            description: 'Get accurate admission predictions for IITs, NITs, IIITs, GFTIs, and top private engineering colleges based on your JEE Main and JEE Advanced ranks. Our algorithm analyzes 5+ years of cutoff data.',
            icon: '🎓'
          },
          {
            title: 'Historical Data Analysis',
            description: 'Access comprehensive 5-year cutoff trends, seat availability, and admission statistics for thousands of colleges across India. Make informed decisions with data-backed insights.',
            icon: '📊'
          },
          {
            title: 'Personalized PDF Reports',
            description: 'Receive detailed, customized PDF reports with your college predictions, cutoff comparisons, and admission probability analysis directly in your email inbox.',
            icon: '📄'
          },
          {
            title: 'Referral Rewards Program',
            description: 'Earn ₹200 for every friend you refer who uses our services. Share the benefits of accurate college predictions with your peers and get rewarded for helping them succeed.',
            icon: '🤝'
          }
        ]
      },
      history: {
        title: 'Our Story',
        content: 'Founded in 2024 in Chandigarh, College Kampus was born from a simple yet powerful idea: every student deserves transparent and accurate information about college admissions. What started as a passion project by education technology enthusiasts has quickly grown into one of India\'s most trusted college prediction platforms. Today, we serve thousands of students across the country, helping them navigate the complex world of engineering admissions with confidence. Our team of data scientists, educators, and technologists work tirelessly to ensure our predictions are accurate, our platform is user-friendly, and our support is always available when students need us most.'
      },
      team: {
        title: 'Our Team',
        description: 'Meet the dedicated professionals behind College Kampus',
        members: [
          {
            name: 'Rajesh Kumar',
            role: 'Founder & CEO',
            description: 'Education technology expert with 10+ years of experience in college admissions consulting and data analytics',
            image: null
          },
          {
            name: 'Priya Sharma',
            role: 'Chief Technology Officer',
            description: 'AI and machine learning specialist focused on predictive algorithms and data science for education',
            image: null
          },
          {
            name: 'Amit Patel',
            role: 'Head of Operations',
            description: 'Operations leader ensuring seamless customer experience and platform reliability',
            image: null
          }
        ]
      },
      contact: {
        title: 'Get In Touch',
        email: 'contact@collegekampus.com',
        phone: '+91 9056161621',
        address: 'Chandigarh, India',
        socialLinks: {
          facebook: 'https://facebook.com/collegekampus',
          twitter: 'https://twitter.com/collegekampus',
          instagram: 'https://instagram.com/collegekampus',
          linkedin: 'https://linkedin.com/company/collegekampus'
        }
      },
      companyInfo: {
        title: 'Company Information',
        founded: '2024',
        headquarters: 'Chandigarh, India',
        employees: '10-50 professionals',
        website: 'www.collegekampus.com'
      },
      footer: {
        copyrightText: '© 2024 College Kampus. All rights reserved.',
        links: [
          { text: 'Privacy Policy', url: '/privacy' },
          { text: 'Terms of Service', url: '/terms' },
          { text: 'Contact Us', url: '/about-us' }
        ]
      }
    }
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const sections = pageContent?.sections || getDefaultContent().sections;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="bg-blue-600 p-2 rounded-lg">
              <span className="text-white text-xl font-bold">CK</span>
            </div>
            <span className="text-xl font-bold text-gray-800">College Kampus</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-gray-600 hover:text-blue-600 transition">
              Home
            </button>
            <button className="text-blue-600 font-semibold">
              About Us
            </button>
            <Button onClick={() => navigate('/user-login')}>Login</Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            {sections.hero.title}
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            {sections.hero.subtitle}
          </p>
          <p className="text-lg text-gray-500">
            {sections.hero.description}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-blue-50 p-8 rounded-xl">
              <h2 className="text-3xl font-bold text-blue-600 mb-4">
                {sections.mission.title}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {sections.mission.content}
              </p>
            </div>
            <div className="bg-purple-50 p-8 rounded-xl">
              <h2 className="text-3xl font-bold text-purple-600 mb-4">
                {sections.vision.title}
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed">
                {sections.vision.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            {sections.services.title}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sections.services.items.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition">
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-8">
            {sections.history.title}
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed text-center">
            {sections.history.content}
          </p>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            {sections.team.title}
          </h2>
          <p className="text-gray-600 text-center mb-12">
            {sections.team.description}
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {sections.team.members.map((member, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-white text-3xl font-bold">
                    {member.name.charAt(0)}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-blue-600 font-semibold mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600 text-sm">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info & Contact */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Company Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {sections.companyInfo.title}
              </h2>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 font-semibold w-32">Founded:</span>
                  <span className="text-gray-800">{sections.companyInfo.founded}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 font-semibold w-32">Headquarters:</span>
                  <span className="text-gray-800">{sections.companyInfo.headquarters}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 font-semibold w-32">Team Size:</span>
                  <span className="text-gray-800">{sections.companyInfo.employees}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 font-semibold w-32">Website:</span>
                  <span className="text-blue-600">{sections.companyInfo.website}</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {sections.contact.title}
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="text-2xl">📧</span>
                  <div>
                    <p className="text-gray-600 text-sm">Email</p>
                    <p className="text-gray-800 font-semibold">{sections.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-2xl">📱</span>
                  <div>
                    <p className="text-gray-600 text-sm">Phone</p>
                    <p className="text-gray-800 font-semibold">{sections.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-2xl">📍</span>
                  <div>
                    <p className="text-gray-600 text-sm">Address</p>
                    <p className="text-gray-800 font-semibold">{sections.contact.address}</p>
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <a href={sections.contact.socialLinks.facebook} className="text-3xl hover:scale-110 transition">📘</a>
                  <a href={sections.contact.socialLinks.twitter} className="text-3xl hover:scale-110 transition">🐦</a>
                  <a href={sections.contact.socialLinks.instagram} className="text-3xl hover:scale-110 transition">📷</a>
                  <a href={sections.contact.socialLinks.linkedin} className="text-3xl hover:scale-110 transition">💼</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;
